import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../../axios/axiosInstance";
import { toast } from "react-toastify";
import { VehicleLog } from "../vehicle-logs/types";
import { campusApi } from "../campus/campus-api";
import { Campus, Node, Camera } from "../campus/types";
import HlsPlayer from "../video/HLSPlayer";

interface DashboardCamera {
  id: number;
  cameraId: number;
  nodeId: string;
  campusId: number;
  streamUrl?: string;
  camera?: Camera;
  node?: Node;
  campus?: Campus;
}

export default function DashboardPage() {
  const navigate = useNavigate();
  const [showMenu, setShowMenu] = useState(false);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [showBuildingSubmenu, setShowBuildingSubmenu] = useState(false);
  const [showMessagingSubmenu, setShowMessagingSubmenu] = useState(false);
  const [showAddCameraModal, setShowAddCameraModal] = useState(false);
  const [selectedCameraIds, setSelectedCameraIds] = useState<number[]>([]);
  const [recentVisitors, setRecentVisitors] = useState<any[]>([]);
  const [dashboardCameras, setDashboardCameras] = useState<DashboardCamera[]>([]);
  const [recentVehicles, setRecentVehicles] = useState<VehicleLog[]>([]);
  const [loadingVisitors, setLoadingVisitors] = useState(false);
  const [loadingCameras, setLoadingCameras] = useState(false);
  const [savingCameras, setSavingCameras] = useState(false);
  const [last_visitors_count, setLastVisitorsCount] = useState(5);
  const [campuses, setCampuses] = useState<Campus[]>([]);
  const [nodes, setNodes] = useState<Node[]>([]);
  const [cameras, setCameras] = useState<Camera[]>([]);
  const [selectedCampusId, setSelectedCampusId] = useState<number | null>(null);
  const [selectedNodeId, setSelectedNodeId] = useState<number | null>(null);
  const [loadingCampuses, setLoadingCampuses] = useState(false);
  const [loadingNodes, setLoadingNodes] = useState(false);
  const [loadingCamerasList, setLoadingCamerasList] = useState(false);

  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const hlsPort = 8888;

  useEffect(() => {
    const fetchRecentVisitors = async () => {
      setLoadingVisitors(true);
      try {
        const response = await axiosInstance.get(`/api/profile/me/last-visitors`) as any;
        if (response?.data?.status === "ok" && response?.data?.data?.visitors) {
          setRecentVisitors(response.data.data.visitors);
        }
      } catch (error) {
        console.error("Failed to fetch recent visitors:", error);
      } finally {
        setLoadingVisitors(false);
      }
    };

    fetchRecentVisitors();
  }, []);

  const transformEntryLog = async (entryLog: any): Promise<VehicleLog> => {
    const log: VehicleLog = {
      id: entryLog.vehicle_log_id || Date.now(),
      vehicle_log_id: String(entryLog.vehicle_log_id),
      number_plate: entryLog.number_plate || entryLog.vehicle?.reg_no || "",
      reg_no: entryLog.vehicle?.reg_no || entryLog.number_plate,
      vehicle_type: entryLog.vehicle_details?.vehicle_type || entryLog.vehicle?.type || null,
      make: entryLog.vehicle_details?.make || null,
      color: entryLog.vehicle_details?.primary_color || null,
      primary_color: entryLog.vehicle_details?.primary_color,
      secondary_colors: entryLog.vehicle_details?.secondary_colors,
      date: entryLog.entry?.time || entryLog.exit?.time || new Date().toISOString(),
      timestamp: entryLog.entry?.time || entryLog.exit?.time || new Date().toISOString(),
      is_inside: entryLog.status === "entry" && entryLog.exit === null,
      status: entryLog.status || (entryLog.exit ? "exit" : "entry"),
      entry_time: entryLog.entry?.time,
      exit_time: entryLog.exit?.time,
      entry_camera_id: entryLog.entry?.camera_id,
      exit_camera_id: entryLog.exit?.camera_id,
      cam_id: entryLog.entry?.camera_id || entryLog.exit?.camera_id,
      vehicle: entryLog.vehicle,
      confidence_score: entryLog.confidence_score,
    };
    return log;
  };

  useEffect(() => {
    const connectWebSocket = () => {
      try {
        if (wsRef.current) return;

        const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
        const host = window.location.host;
        const wsUrl = `${protocol}//localhost:3070/api/ws/vehicle-logs?access_token=${localStorage.getItem("token")}`;

        const ws = new WebSocket(wsUrl);
        wsRef.current = ws;

        ws.onopen = () => {
          console.log("Connected to vehicle logs WebSocket");
          if (reconnectTimeoutRef.current) {
            clearTimeout(reconnectTimeoutRef.current);
            reconnectTimeoutRef.current = null;
          }
        };

        ws.onmessage = async (event) => {
          try {
            const data = JSON.parse(event.data);

            if (data.type === "vehicle:logs:initial" && Array.isArray(data.entry_logs)) {
              console.log(`Received initial message with ${data.entry_logs.length} logs`);

              const transformedLogs = await Promise.all(
                data.entry_logs.slice(0,last_visitors_count).map((entryLog: any) => transformEntryLog(entryLog))
              );

              setRecentVehicles(transformedLogs);
              return;
            }

            const log: VehicleLog = {
              id: data.vehicle_log_id || data.id || Date.now(),
              vehicle_log_id: String(data.vehicle_log_id || data.id),
              number_plate: data.number_plate || data.vehicle?.reg_no || "",
              reg_no: data.vehicle?.reg_no || data.reg_no || data.number_plate,
              vehicle_type: data.vehicle_details?.vehicle_type || data.vehicle?.type || data.vehicle_type || data.type,
              make: data.vehicle_details?.make || data.make,
              color: data.vehicle_details?.primary_color || data.color,
              primary_color: data.vehicle_details?.primary_color,
              secondary_colors: data.vehicle_details?.secondary_colors,
              date: data.entry?.time || data.exit?.time || data.date || data.timestamp || new Date().toISOString(),
              timestamp: data.entry?.time || data.exit?.time || data.timestamp || data.date || new Date().toISOString(),
              is_inside: data.is_inside !== undefined ? data.is_inside : (data.status === "entry" && !data.exit),
              status: data.status || (data.exit ? "exit" : "entry"),
              entry_time: data.entry?.time || data.entry_time,
              exit_time: data.exit?.time || data.exit_time,
              entry_camera_id: data.entry?.camera_id,
              exit_camera_id: data.exit?.camera_id,
              cam_id: data.entry?.camera_id || data.exit?.camera_id || data.cam_id,
              vehicle: data.vehicle,
              owner: data.owner,
              member: data.member,
              label: data.label,
              node: data.node,
              confidence_score: data.confidence_score,
            };

            setRecentVehicles((prev) => [log, ...prev].slice(0, last_visitors_count));
          } catch (error) {
            console.error("Error parsing WebSocket message:", error);
          }
        };

        ws.onerror = (error) => {
          console.error("WebSocket error:", error);
        };

        ws.onclose = () => {
          reconnectTimeoutRef.current = setTimeout(() => {
            wsRef.current = null;
            connectWebSocket();
          }, 3000);
        };
      } catch (error) {
        console.error("Failed to connect WebSocket:", error);
      }
    };

    connectWebSocket();

    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
    };
  }, []);
  useEffect(() => {
    fetchDashboardCameras();
  }, []);

  useEffect(() => {
    if (showAddCameraModal) {
      fetchCampuses();
    }
  }, [showAddCameraModal]);

  useEffect(() => {
    if (selectedCampusId) {
      fetchNodes(selectedCampusId);
      setSelectedNodeId(null);
      setCameras([]);
    }
  }, [selectedCampusId]);

  useEffect(() => {
    if (selectedNodeId) {
      fetchCamerasByNode(selectedNodeId);
    }
  }, [selectedNodeId]);

  const fetchDashboardCameras = async () => {
    setLoadingCameras(true);
    try {
      const response: any = await axiosInstance.get(`/api/profile/me/dashboard-settings`);
      if (response?.data?.status === "ok" && response?.data?.data?.cameras) {
        const cameraIds = response.data.data.cameras;
        const camerasWithDetails = await Promise.all(
          cameraIds.map(async (camera: any) => {
            try {
              const streamUrl = camera.cam_id
                ? `http://localhost:${hlsPort}/${camera.cam_id}/index.m3u8`
                : undefined;

              return {
                id: camera.cam_id,
                cameraId: camera.id,
                nodeId: camera.nodeId,
                streamUrl,
                camera,
                node: camera.node,
                campus: camera.node.campus,
              };
            } catch (error) {
              console.error(`Code:110, Failed to fetch camera ${camera.id}:`, error);
              return null;
            }
          })
        );
        setDashboardCameras(camerasWithDetails.filter((c): c is DashboardCamera => c !== null && !!c.camera));
        setLastVisitorsCount(response.data.data.last_visitors_count);
      }
    } catch (error) {
      console.error("Failed to fetch dashboard cameras:", error);
    } finally {
      setLoadingCameras(false);
    }
  };

  const saveDashboardCameras = async (cameraIds: number[]) => {
    setSavingCameras(true);
    try {
      const response: any = await axiosInstance.put(`/api/profile/me/dashboard-settings`, {
        cameras: cameraIds,
      });
      if (response?.data?.status === "ok") {
        toast.success("Dashboard cameras updated successfully");
        await fetchDashboardCameras();
        setShowAddCameraModal(false);
        setSelectedCameraIds([]);
      }
    } catch (error: any) {
      toast.error(error?.response?.data?.message || "Failed to save dashboard cameras");
    } finally {
      setSavingCameras(false);
    }
  };

  const fetchCampuses = async () => {
    setLoadingCampuses(true);
    try {
      const data = await campusApi.getAllCampuses();
      setCampuses(data);
    } catch (error: any) {
      toast.error("Failed to load campuses");
    } finally {
      setLoadingCampuses(false);
    }
  };

  const fetchNodes = async (campusId: number) => {
    setLoadingNodes(true);
    try {
      const data = await campusApi.getNodesByCampus(campusId);
      setNodes(data);
    } catch (error: any) {
      toast.error("Failed to load nodes");
      setNodes([]);
    } finally {
      setLoadingNodes(false);
    }
  };

  const fetchCamerasByNode = async (nodeId: number) => {
    setLoadingCamerasList(true);
    try {
      const data = await campusApi.getCamerasByNode(nodeId);
      setCameras(data);
    } catch (error: any) {
      toast.error("Failed to load cameras");
      setCameras([]);
    } finally {
      setLoadingCamerasList(false);
    }
  };

  const handleAddCamera = () => {
    setShowAddCameraModal(true);
    setSelectedCameraIds(dashboardCameras.map(c => c.cameraId));
  };

  const handleCameraToggle = (cameraId: number) => {
    setSelectedCameraIds((prev) =>
      prev.includes(cameraId)
        ? prev.filter((id) => id !== cameraId)
        : [...prev, cameraId]
    );
  };

  const handleSaveCameras = () => {
    saveDashboardCameras(selectedCameraIds);
  };

  const handleRemoveCamera = async (cameraId: number) => {
    const updatedIds = dashboardCameras
      .filter((c) => c.cameraId !== cameraId)
      .map((c) => c.cameraId);
    await saveDashboardCameras(updatedIds);
  };

  return (
    <main className="flex-1">
      <div className="p-6">
        <div className="mb-6">
          <h1 className="text-3xl font-semibold mb-2 dark:text-white">
            Dashboard
          </h1>
        </div>

        <div className="mb-6">
          <div className="bg-white dark:bg-neutral-800 rounded-xl shadow-lg border dark:border-neutral-700">
            <div className="p-4 border-b dark:border-neutral-700 flex items-center justify-between">
              <h2 className="text-lg font-semibold dark:text-white flex items-center gap-2">
                <i className="fa-solid fa-video text-red-500"></i>
                Live Cameras
              </h2>
              <button
                onClick={handleAddCamera}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
              >
                <i className="fa-solid fa-plus"></i>
                Manage Cameras
              </button>
            </div>
            <div className="p-4">
              {loadingCameras ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
                </div>
              ) : dashboardCameras.length === 0 ? (
                <div className="text-center py-12 text-neutral-500 dark:text-neutral-400">
                  No cameras configured. Click "Manage Cameras" to add cameras.
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  {dashboardCameras.map((dashboardCamera) => (
                    <div
                      key={dashboardCamera.id}
                      className="bg-neutral-50 dark:bg-neutral-700 rounded-lg overflow-hidden relative group"
                    >
                      <div className="p-3 flex items-center justify-between border-b dark:border-neutral-600">
                        <div className="flex-1 min-w-0">
                          <div className="font-medium dark:text-white truncate">
                            {dashboardCamera.camera?.type == "both" ? "Entry & Exit" : dashboardCamera.camera?.type == "entry" ? "Entry" : "Exit"}: {dashboardCamera.camera?.cam_id}
                          </div>
                          {dashboardCamera.campus && (
                            <div className="text-xs text-neutral-600 dark:text-neutral-400 truncate">
                              {dashboardCamera.campus.name}
                            </div>
                          )}
                          {dashboardCamera.node && (
                            <div className="text-xs text-neutral-500 dark:text-neutral-500 truncate">
                              {dashboardCamera.node.node_name}
                            </div>
                          )}
                        </div>
                        <button
                          onClick={() => handleRemoveCamera(dashboardCamera.cameraId)}
                          className="p-1 text-neutral-500 hover:text-red-500 dark:text-neutral-400 dark:hover:text-red-400 transition-colors ml-2 flex-shrink-0"
                          title="Remove camera"
                        >
                          <i className="fa-solid fa-xmark"></i>
                        </button>
                      </div>
                      <div className="aspect-video bg-black relative">
                        {dashboardCamera.streamUrl ? (
                          <HlsPlayer
                            src={dashboardCamera.streamUrl}
                            autoPlay
                            muted
                            resizable={false}
                            maximizable={true}
                            className="w-full h-full"
                          />
                        ) : (
                          <div className="absolute inset-0 flex items-center justify-center text-white">
                            <i className="fa-solid fa-video text-4xl"></i>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {showAddCameraModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white dark:bg-neutral-800 rounded-xl shadow-lg w-full max-w-6xl max-h-[90vh] flex flex-col">
              <div className="p-4 border-b dark:border-neutral-700 flex items-center justify-between">
                <h3 className="text-lg font-semibold dark:text-white">
                  Select Cameras for Dashboard
                </h3>
                <button
                  onClick={() => {
                    setShowAddCameraModal(false);
                    setSelectedCameraIds([]);
                    setSelectedCampusId(null);
                    setSelectedNodeId(null);
                    setCameras([]);
                  }}
                  className="text-neutral-500 hover:text-neutral-700 dark:text-neutral-400 dark:hover:text-neutral-200"
                >
                  <i className="fa-solid fa-xmark text-xl"></i>
                </button>
              </div>
              <div className="p-6 flex-1 overflow-y-auto">
                <div className="mb-6 grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Campus
                    </label>
                    <select
                      value={selectedCampusId || ""}
                      onChange={(e) => setSelectedCampusId(Number(e.target.value) || null)}
                      disabled={loadingCampuses}
                      className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">Select Campus</option>
                      {campuses.map((campus) => (
                        <option key={campus.id} value={campus.id}>
                          {campus.name} - {campus.location}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Node
                    </label>
                    <select
                      value={selectedNodeId ?? ""}
                      onChange={(e) => {
                        const value = e.target.value;
                        setSelectedNodeId(value ? Number(value) : null);
                      }}
                      disabled={!selectedCampusId || loadingNodes}
                      className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50"
                    >
                      <option value="">Select Node</option>
                      {nodes.map((node) => (
                        <option key={node.node_id} value={node.id}>
                          {node.node_name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="flex items-end">
                    <div className="text-sm text-neutral-600 dark:text-neutral-400">
                      {selectedCameraIds.length} camera(s) selected
                    </div>
                  </div>
                </div>

                {selectedNodeId && (
                  <div>
                    {loadingCamerasList ? (
                      <div className="flex items-center justify-center py-8">
                        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
                      </div>
                    ) : cameras.length === 0 ? (
                      <div className="text-center py-8 text-neutral-500 dark:text-neutral-400">
                        No cameras found for this node
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {cameras.map((camera) => (
                          <div
                            key={camera.id}
                            className="flex items-center justify-between p-4 bg-neutral-50 dark:bg-neutral-700 rounded-lg hover:bg-neutral-100 dark:hover:bg-neutral-600 transition-colors"
                          >
                            <div className="flex-1">
                              <div className="font-medium dark:text-white">
                                {camera.cam_id || `Camera ${camera.id}`}
                              </div>
                              <div className="text-sm text-neutral-600 dark:text-neutral-400">
                                Type: {camera.type} {camera.vehicleDetectionEnabled && "• Vehicle Detection"}
                              </div>
                              {camera.rtspUrl && (
                                <div className="text-xs text-neutral-500 dark:text-neutral-500 truncate">
                                  {camera.rtspUrl}
                                </div>
                              )}
                            </div>
                            <button
                              onClick={() => handleCameraToggle(camera.id)}
                              className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ml-4 flex-shrink-0 ${selectedCameraIds.includes(camera.id)
                                ? "border-blue-600 bg-blue-600 dark:border-blue-500 dark:bg-blue-500"
                                : "border-neutral-300 dark:border-neutral-600"
                                }`}
                            >
                              {selectedCameraIds.includes(camera.id) && (
                                <i className="fa-solid fa-check text-white text-xs"></i>
                              )}
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {!selectedNodeId && (
                  <div className="text-center py-12 text-neutral-500 dark:text-neutral-400">
                    Please select a campus and node to view cameras
                  </div>
                )}
              </div>
              <div className="p-4 border-t dark:border-neutral-700 flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setShowAddCameraModal(false);
                    setSelectedCameraIds([]);
                    setSelectedCampusId(null);
                    setSelectedNodeId(null);
                    setCameras([]);
                  }}
                  className="px-4 py-2 text-neutral-700 dark:text-neutral-300 hover:text-neutral-900 dark:hover:text-white"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveCameras}
                  disabled={selectedCameraIds.length === 0 || savingCameras}
                  className={`px-4 py-2 rounded-lg ${selectedCameraIds.length > 0 && !savingCameras
                    ? "bg-blue-600 text-white hover:bg-blue-700"
                    : "bg-neutral-200 text-neutral-500 dark:bg-neutral-700 dark:text-neutral-400 cursor-not-allowed"
                    }`}
                >
                  {savingCameras ? "Saving..." : `Save ${selectedCameraIds.length} Camera(s)`}
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white dark:bg-neutral-800 rounded-xl shadow-lg border dark:border-neutral-700">
            <div className="p-4 border-b dark:border-neutral-700">
              <h2 className="text-lg font-semibold dark:text-white flex items-center gap-2">
                <i className="fa-solid fa-person-walking text-blue-500"></i>
                Recent Visitors
              </h2>
            </div>
            <div className="p-4 flex-1 overflow-y-auto max-h-[600px]">
              {loadingVisitors ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
                </div>
              ) : recentVisitors.length === 0 ? (
                <div className="text-center py-8 text-neutral-500 dark:text-neutral-400">
                  No recent visitors
                </div>
              ) : (
                <div className="space-y-4">
                  {recentVisitors.map((visitLog) => (
                    <div
                      key={visitLog.id}
                      className="p-3 bg-neutral-50 dark:bg-neutral-700 rounded-lg hover:bg-neutral-100 dark:hover:bg-neutral-600 transition-colors"
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex-1">
                          <div className="font-medium dark:text-white text-lg">
                            {visitLog.visitor?.visitor_full_name || visitLog.member?.name || "Unknown"}
                          </div>
                          {visitLog.visitor?.phone && (
                            <div className="text-sm text-neutral-600 dark:text-neutral-400">
                              {visitLog.visitor.phone}
                            </div>
                          )}
                          {visitLog.visitor?.email && (
                            <div className="text-xs text-neutral-500 dark:text-neutral-500">
                              {visitLog.visitor.email}
                            </div>
                          )}
                          {visitLog.is_member && (
                            <span className="inline-block mt-1 px-2 py-0.5 rounded-full text-xs bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300">
                              Member
                            </span>
                          )}
                        </div>
                        <span
                          className={`px-2 py-1 rounded-full text-xs font-medium ${visitLog.exit_time === null
                            ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300"
                            : "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300"
                            }`}
                        >
                          {visitLog.exit_time === null ? "Inside" : "Exited"}
                        </span>
                      </div>

                      <div className="mt-2 space-y-1 text-sm">
                        {visitLog.check_in && (
                          <div className="text-neutral-600 dark:text-neutral-400">
                            <span className="font-medium">Check-in:</span>{" "}
                            {new Date(visitLog.check_in).toLocaleString()}
                          </div>
                        )}
                        {visitLog.check_out && (
                          <div className="text-neutral-600 dark:text-neutral-400">
                            <span className="font-medium">Check-out:</span>{" "}
                            {new Date(visitLog.check_out).toLocaleString()}
                          </div>
                        )}
                        {visitLog.entry_time && (
                          <div className="text-neutral-600 dark:text-neutral-400">
                            <span className="font-medium">Entry:</span>{" "}
                            {new Date(visitLog.entry_time).toLocaleString()}
                          </div>
                        )}
                        {visitLog.exit_time && (
                          <div className="text-neutral-600 dark:text-neutral-400">
                            <span className="font-medium">Exit:</span>{" "}
                            {new Date(visitLog.exit_time).toLocaleString()}
                          </div>
                        )}
                        {visitLog.to_meet && visitLog.to_meet.length > 0 && (
                          <div className="text-neutral-600 dark:text-neutral-400">
                            <span className="font-medium">To meet:</span>{" "}
                            {visitLog.to_meet.map((person: any) => person.name).join(", ")}
                          </div>
                        )}
                        {visitLog.notes && (
                          <div className="text-neutral-600 dark:text-neutral-400 mt-2 pt-2 border-t dark:border-neutral-600">
                            <span className="font-medium">Notes:</span> {visitLog.notes}
                          </div>
                        )}
                        {visitLog.vehicle_log && (
                          <div className="text-neutral-600 dark:text-neutral-400 mt-2 pt-2 border-t dark:border-neutral-600">
                            <div className="font-medium">Vehicle:</div>
                            <div className="text-xs">
                              {visitLog.vehicle_log.number_plate || visitLog.vehicle_log.vehicle?.reg_no}
                              {visitLog.vehicle_log.vehicle?.type && (
                                <span> • {visitLog.vehicle_log.vehicle.type}</span>
                              )}
                            </div>
                            {visitLog.vehicle_log.entry_time && (
                              <div className="text-xs text-neutral-500 dark:text-neutral-500">
                                Entry: {new Date(visitLog.vehicle_log.entry_time).toLocaleString()}
                              </div>
                            )}
                          </div>
                        )}
                        {visitLog.require_follow_up && (
                          <div className="mt-2">
                            <span className="px-2 py-1 rounded-full text-xs bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300">
                              Follow-up Required
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="bg-white dark:bg-neutral-800 rounded-xl shadow-lg border dark:border-neutral-700">
            <div className="p-4 border-b dark:border-neutral-700">
              <h2 className="text-lg font-semibold dark:text-white flex items-center gap-2">
                <i className="fa-solid fa-car text-green-500"></i>
                Recent Vehicles
              </h2>
            </div>
            <div className="p-4 flex-1 overflow-y-auto max-h-[600px]">
              <div className="space-y-4">
                {recentVehicles.length === 0 ? (
                  <div className="text-center py-8 text-neutral-500 dark:text-neutral-400">
                    No vehicle logs yet
                  </div>
                ) : (
                  recentVehicles.map((log) => (
                    <div
                      key={log.vehicle_log_id}
                      className="p-3 bg-neutral-50 dark:bg-neutral-700 rounded-lg hover:bg-neutral-100 dark:hover:bg-neutral-600 transition-colors"
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex-1">
                          <div className="font-medium dark:text-white text-lg">
                            {log.number_plate || log.reg_no || log.vehicle?.reg_no}
                          </div>
                          {log.vehicle_type && (
                            <div className="text-sm text-neutral-600 dark:text-neutral-400">
                              {log.vehicle_type}
                              {log.make && ` • ${log.make}`}
                            </div>
                          )}
                          {log.primary_color && (
                            <div className="text-xs text-neutral-500 dark:text-neutral-500">
                              Color: {log.primary_color}
                              {log.secondary_colors && log.secondary_colors.length > 0 && (
                                <span> ({log.secondary_colors.join(", ")})</span>
                              )}
                            </div>
                          )}
                        </div>
                        <span
                          className={`px-2 py-1 rounded-full text-xs font-medium ${log.status === "entry"
                            ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300"
                            : "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300"
                            }`}
                        >
                          {log.status === "entry" ? "Entry" : "Exit"}
                        </span>
                      </div>

                      <div className="mt-2 space-y-1 text-sm">
                        {log.entry_time && (
                          <div className="text-neutral-600 dark:text-neutral-400">
                            <span className="font-medium">Entry:</span>{" "}
                            {new Date(log.entry_time).toLocaleString()}
                          </div>
                        )}
                        {log.exit_time && (
                          <div className="text-neutral-600 dark:text-neutral-400">
                            <span className="font-medium">Exit:</span>{" "}
                            {new Date(log.exit_time).toLocaleString()}
                          </div>
                        )}
                        {log.entry_campus && (
                          <div className="text-neutral-600 dark:text-neutral-400">
                            <span className="font-medium">Campus:</span> {log.entry_campus.name}
                          </div>
                        )}
                        {log.entry_node && (
                          <div className="text-neutral-600 dark:text-neutral-400">
                            <span className="font-medium">Entry Node:</span> {log.entry_node.node_name}
                          </div>
                        )}
                        {log.entry_camera_id && (
                          <div className="text-xs text-neutral-500 dark:text-neutral-500">
                            Entry Camera: {log.entry_camera_id}
                          </div>
                        )}
                        {log.exit_node && (
                          <div className="text-neutral-600 dark:text-neutral-400">
                            <span className="font-medium">Exit Node:</span> {log.exit_node.node_name}
                          </div>
                        )}
                        {log.exit_camera_id && (
                          <div className="text-xs text-neutral-500 dark:text-neutral-500">
                            Exit Camera: {log.exit_camera_id}
                          </div>
                        )}
                        {log.vehicle?.user && (
                          <div className="text-neutral-600 dark:text-neutral-400 mt-2 pt-2 border-t dark:border-neutral-600">
                            <div className="font-medium dark:text-white">
                              {log.vehicle.user.name}
                            </div>
                            {log.vehicle.user.phone && (
                              <div className="text-xs">{log.vehicle.user.phone}</div>
                            )}
                            <div className="text-xs mt-1">
                              {log.vehicle.visitor ? (
                                <span className="text-orange-600">Visitor</span>
                              ) : (
                                <span className="text-green-600">
                                  {log.vehicle.user.member_type || "Member"}
                                </span>
                              )}
                              {log.vehicle.user.is_leader && (
                                <span className="text-blue-600 ml-2">Leader</span>
                              )}
                            </div>
                          </div>
                        )}
                        {log.confidence_score && (
                          <div className="text-xs text-neutral-500 dark:text-neutral-500 mt-1">
                            Confidence: {log.confidence_score}%
                          </div>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
}
